<template>
  <div class="pricing-container">
    <div class="pricing-header">
      <div class="header-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      </div>
      <h1>Árazás Kezelése</h1>
      <p>Jegyek és árak beállítása, részletfizetések kezelése</p>
    </div>

    <div class="pricing-content">
      <!-- Tickets Management Section -->
      <div class="section">
        <div class="section-header">
          <h2>Jegyek Kezelése</h2>
          <button @click="showCreateTicketmdl = true" class="btn-primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Új Jegy
          </button>
        </div>

        <div class="tickets-grid">
          <div v-for="ticket in tickets" :key="ticket.id" class="ticket-card" :class="{ inactive: !ticket.is_active }">
            <div class="ticket-header">
              <h3>{{ ticket.name }}</h3>
              <div class="ticket-actions">
                <button @click="editTicket(ticket)" class="btn-edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button @click="deleteTicket(ticket.id)" class="btn-delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3,6 5,6 21,6"/>
                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="ticket-content">
              <p class="ticket-description">{{ ticket.description }}</p>
              <div class="ticket-price">
                <span class="price">{{ formatPrice(ticket.price) }}</span>
                <span class="ticket-type">{{ ticket.ticket_type }}</span>
              </div>
              <div class="ticket-events">
                <span class="events-count">{{ ticket.events_per_ticket }} esemény</span>
              </div>
              <div class="ticket-status">
                <span :class="ticket.is_active ? 'status-active' : 'status-inactive'">
                  {{ ticket.is_active ? 'Aktív' : 'Inaktív' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Management Section -->
      <div class="section">
        <div class="section-header">
          <h2>Fizetések Kezelése</h2>
        </div>
        <div class="payment-filters">
          <select v-model="selectedChild" @change="loadChildPayments">
            <option value="">Válassz gyereket...</option>
            <option v-for="child in children" :key="child.id" :value="child.id">
              {{ child.fullName }}
            </option>
          </select>
        </div>

        <div v-if="selectedChild && childPayments.length > 0" class="payments-list">
          <div v-for="payment in childPayments" :key="payment.id" class="payment-card">
            <div class="payment-header">
              <h4>Jegy #{{ payment.ticket_id }}</h4>
              <span :class="'status-' + payment.status">{{ getStatusText(payment.status) }}</span>
            </div>
            <div class="payment-details">
              <div class="payment-amounts">
                <div class="amount-paid">
                  <span class="label">Fizetve:</span>
                  <span class="amount">{{ formatPrice(payment.amount_paid) }}</span>
                </div>
                <div class="amount-due">
                  <span class="label">Hátralék:</span>
                  <span class="amount">{{ formatPrice(payment.amount_due) }}</span>
                </div>
              </div>
              <div class="payment-info">
                <p><strong>Fizetési mód:</strong> {{ payment.payment_method }}</p>
                <p><strong>Dátum:</strong> {{ formatDate(payment.payment_date) }}</p>
                <p v-if="payment.notes"><strong>Megjegyzés:</strong> {{ payment.notes }}</p>
              </div>
              <div class="payment-actions">
                <button @click="addAdditionalPayment(payment)" class="btn-secondary" v-if="payment.amount_due > 0">
                  További fizetés
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="selectedChild && childPayments.length === 0" class="no-payments">
          <p>Nincsenek fizetések ehhez a gyerekhez.</p>
        </div>
      </div>

      <!-- Group price catalog section -->
      <div class="section">
        <div class="section-header">
          <h2>Csoport dijszabás</h2>
          <button @click="saveAllGroupPrices" class="btn-primary" :disabled="savingGroupPrices">
            {{ savingGroupPrices ? 'Mentés...' : 'Összes mentése' }}
          </button>
        </div>
        <p class="section-hint">Állítsd be az egyes csoportok óránkénti és havi alapárát (HUF). Ezek az árak szolgálnak alapul a számlázásnál.</p>
        <div v-if="groupPriceRows.length > 0" class="group-price-table-wrap">
          <table class="group-price-table">
            <thead>
              <tr>
                <th>Csoport</th>
                <th>Óra ára (Ft)</th>
                <th>Havi ár (Ft)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in groupPriceRows" :key="row.group_id">
                <td class="group-name">{{ row.name }}</td>
                <td>
                  <input type="number" step="0.01" min="0" v-model.number="row.per_class" placeholder="0" />
                </td>
                <td>
                  <input type="number" step="0.01" min="0" v-model.number="row.monthly" placeholder="0" />
                </td>
                <td>
                  <button class="btn-primary btn-sm" @click="saveGroupPrice(row)" :disabled="savingGroupPrices">
                    {{ row.saving ? 'Mentés...' : 'Mentés' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="no-payments">
          <p>Nincsenek elérhető csoportok az árazáshoz.</p>
        </div>
      </div>
    </div>

    <!-- Create/Edit Ticket mdl -->
    <div v-if="showCreateTicketmdl || showEditTicketmdl" class="mdl-overlay" @click="closemdls">
      <div class="mdl-content" @click.stop>
        <div class="mdl-header">
          <h3>{{ showCreateTicketmdl ? 'Új Jegy Létrehozása' : 'Jegy Szerkesztése' }}</h3>
          <button @click="closemdls" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="mdl-body">
          <form @submit.prevent="saveTicket">
            <div class="form-group">
              <label for="ticketName">Jegy neve:</label>
              <input 
                id="ticketName" 
                v-model="ticketForm.name" 
                type="text" 
                required 
                placeholder="pl. Egyszeri belépő"
              />
            </div>
            <div class="form-group">
              <label for="ticketDescription">Leírás:</label>
              <textarea 
                id="ticketDescription" 
                v-model="ticketForm.description" 
                placeholder="Jegy leírása..."
              ></textarea>
            </div>
            <div class="form-group">
              <label for="ticketPrice">Ár (Ft):</label>
              <input 
                id="ticketPrice" 
                v-model.number="ticketForm.price" 
                type="number" 
                step="0.01" 
                required 
                placeholder="0.00"
              />
            </div>
            <div class="form-group">
              <label for="ticketType">Jegy típusa:</label>
              <select id="ticketType" v-model="ticketForm.ticket_type" required>
                <option value="single">Egyszeri</option>
                <option value="multi">Többszörös</option>
                <option value="monthly">Havi</option>
                <option value="seasonal">Szezonális</option>
              </select>
            </div>
            <div class="form-group">
              <label for="eventsPerTicket">Események száma jegyenként:</label>
              <input 
                id="eventsPerTicket" 
                v-model.number="ticketForm.events_per_ticket" 
                type="number" 
                min="1" 
                required 
                placeholder="pl. 1 = egyszeri, 4 = bérlet"
              />
              <small class="form-help">Hány eseményre jogosít ez a jegy (pl. 1 = egyszeri belépő, 4 = 4 alkalmas bérlet)</small>
            </div>
            <div v-if="showEditTicketmdl" class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="ticketForm.is_active" />
                Aktív
              </label>
            </div>
            <div class="form-actions">
              <button type="button" @click="closemdls" class="btn-secondary">Mégse</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                {{ saving ? 'Mentés...' : 'Mentés' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Add Payment mdl -->
    <div v-if="showPaymentmdl" class="mdl-overlay" @click="closePaymentmdl">
      <div class="mdl-content" @click.stop>
        <div class="mdl-header">
          <h3>További Fizetés Hozzáadása</h3>
          <button @click="closePaymentmdl" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="mdl-body">
          <form @submit.prevent="saveAdditionalPayment">
            <div class="form-group">
              <label for="additionalAmount">Fizetett összeg (Ft):</label>
              <input 
                id="additionalAmount" 
                v-model.number="paymentForm.amount_paid" 
                type="number" 
                step="0.01" 
                required 
                :max="selectedPayment.amount_due"
                placeholder="0.00"
              />
            </div>
            <div class="form-group">
              <label for="paymentMethod">Fizetési mód:</label>
              <select id="paymentMethod" v-model="paymentForm.payment_method" required>
                <option value="cash">Készpénz</option>
                <option value="card">Bankkártya</option>
                <option value="transfer">Átutalás</option>
                <option value="other">Egyéb</option>
              </select>
            </div>
            <div class="form-group">
              <label for="paymentNotes">Megjegyzés:</label>
              <textarea 
                id="paymentNotes" 
                v-model="paymentForm.notes" 
                placeholder="Fizetésről szóló megjegyzés..."
              ></textarea>
            </div>
            <div class="form-actions">
              <button type="button" @click="closePaymentmdl" class="btn-secondary">Mégse</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                {{ saving ? 'Mentés...' : 'Fizetés hozzáadása' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import toastMixin from '../mixins/toast.js';

export default {
  mixins: [toastMixin],
  data() {
    return {
      tickets: [],
      availableTickets: [],
      children: [],
      childPayments: [],
      selectedChild: '',
      showCreateTicketmdl: false,
      showEditTicketmdl: false,
      showPaymentmdl: false,
      saving: false,
      ticketForm: {
        id: null,
        name: '',
        description: '',
        price: 0,
        ticket_type: 'single',
        events_per_ticket: 1,
        is_active: true
      },
      paymentForm: {
        amount_paid: 0,
        payment_method: 'cash',
        notes: ''
      },
      selectedPayment: null,
      groupPriceRows: [],
      savingGroupPrices: false
    };
  },
  created() {
    this.loadTickets();
    this.loadChildren();
    this.loadGroupPrices();
  },
  methods: {
    async loadTickets() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/pricing/tickets');
        this.tickets = response.data.tickets;
      } catch (error) {
        console.error('Error loading tickets:', error);
      }
    },
    async loadChildren() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/settings/children/info');
        this.children = response.data.children;
      } catch (error) {
        console.error('Error loading children:', error);
      }
    },
    async loadChildPayments() {
      if (!this.selectedChild) return;
      
      try {
        const response = await axios.get(this.$BACKEND_HOST + `/api/pricing/children/${this.selectedChild}/payments`);
        this.childPayments = response.data.payments;
      } catch (error) {
        console.error('Error loading child payments:', error);
      }
    },
    async loadGroupPrices() {
      try {
        const [groupsRes, pricesRes] = await Promise.all([
          axios.get(this.$BACKEND_HOST + '/api/settings/groups/info'),
          axios.get(this.$BACKEND_HOST + '/api/billing/group-prices')
        ]);
        const groups = groupsRes.data.groups || [];
        const prices = {};
        (pricesRes.data.prices || []).forEach(p => { prices[p.group_id] = p; });
        this.groupPriceRows = groups.map(g => ({
          group_id: g.id,
          name: g.name,
          per_class: prices[g.id] ? (prices[g.id].price_per_class || 0) : 0,
          monthly: prices[g.id] ? (prices[g.id].price_monthly || 0) : 0,
          saving: false
        }));
      } catch (error) {
        console.error('Error loading group prices:', error);
      }
    },
    async saveGroupPrice(row) {
      if (!row || !row.group_id) return;
      row.saving = true;
      this.savingGroupPrices = true;
      try {
        await axios.put(this.$BACKEND_HOST + '/api/billing/group-prices', {
          group_id: parseInt(row.group_id),
          price_per_class: parseFloat(row.per_class) || 0,
          price_monthly: parseFloat(row.monthly) || 0
        });
        await this.loadGroupPrices();
        this.showToast('success', 'Csoport ár mentve');
      } catch (error) {
        console.error('Error saving group price:', error);
        this.showToast('error', 'Hiba a csoport ár mentésekor: ' + (error.response?.data?.message || error.message));
      }
      row.saving = false;
      this.savingGroupPrices = false;
    },
    async saveAllGroupPrices() {
      if (this.groupPriceRows.length === 0) {
        this.showToast('error', 'Nincsenek menthető csoportok');
        return;
      }
      this.savingGroupPrices = true;
      try {
        await Promise.all(this.groupPriceRows.map(row => axios.put(this.$BACKEND_HOST + '/api/billing/group-prices', {
          group_id: parseInt(row.group_id),
          price_per_class: parseFloat(row.per_class) || 0,
          price_monthly: parseFloat(row.monthly) || 0
        })));
        await this.loadGroupPrices();
        this.showToast('success', 'Minden csoport ár mentve');
      } catch (error) {
        console.error('Error saving all group prices:', error);
        this.showToast('error', 'Hiba a csoport árak mentésekor: ' + (error.response?.data?.message || error.message));
      }
      this.savingGroupPrices = false;
    },
    async saveTicket() {
      this.saving = true;
      try {
        console.log('Saving ticket data:', this.ticketForm);
        if (this.showCreateTicketmdl) {
          // Remove id for new tickets
          const createData = { ...this.ticketForm };
          delete createData.id;
          console.log('Creating ticket with data:', createData);
          await axios.post(this.$BACKEND_HOST + '/api/pricing/tickets', createData);
        } else {
          console.log('Updating ticket with data:', this.ticketForm);
          await axios.put(this.$BACKEND_HOST + '/api/pricing/tickets', this.ticketForm);
        }
        this.closemdls();
        this.loadTickets();
        this.showToast('success', 'Ticket saved successfully');
      } catch (error) {
        console.error('Error saving ticket:', error);
        this.showToast('error', 'Failed to save ticket: ' + (error.response?.data?.message || error.message));
      }
      this.saving = false;
    },
    async deleteTicket(ticketId) {
      if (!confirm('Biztosan törölni szeretnéd ezt a jegyet?')) return;
      
      try {
        await axios.delete(this.$BACKEND_HOST + `/api/pricing/tickets/${ticketId}`);
        this.loadTickets();
      } catch (error) {
        console.error('Error deleting ticket:', error);
      }
    },
    editTicket(ticket) {
      this.ticketForm = { ...ticket };
      this.showEditTicketmdl = true;
    },
    addAdditionalPayment(payment) {
      this.selectedPayment = payment;
      this.paymentForm = {
        amount_paid: 0,
        payment_method: 'cash',
        notes: ''
      };
      this.showPaymentmdl = true;
    },
    async saveAdditionalPayment() {
      this.saving = true;
      try {
        this.paymentForm.child_id = parseInt(this.selectedChild);
        
        await axios.put(this.$BACKEND_HOST + `/api/pricing/payments/${this.selectedPayment.id}`, this.paymentForm);
        
        this.closePaymentmdl();
        this.loadChildPayments();
      } catch (error) {
        console.error('Error adding payment:', error);
      }
      this.saving = false;
    },
    getChildName() {
      const child = this.children.find(c => c.id == this.selectedChild);
      return child ? child.fullName : 'Unknown Child';
    },
    closemdls() {
      this.showCreateTicketmdl = false;
      this.showEditTicketmdl = false;
      this.ticketForm = {
        id: null,
        name: '',
        description: '',
        price: 0,
        ticket_type: 'single',
        events_per_ticket: 1,
        is_active: true
      };
    },
    closePaymentmdl() {
      this.showPaymentmdl = false;
      this.selectedPayment = null;
      this.paymentForm = {
        amount_paid: 0,
        payment_method: 'cash',
        notes: ''
      };
    },
    formatPrice(price) {
      return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF'
      }).format(price);
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('hu-HU');
    },
    getStatusText(status) {
      const statusMap = {
        'completed': 'Teljesítve',
        'partial': 'Részleges',
        'pending': 'Függőben'
      };
      return statusMap[status] || status;
    }
  }
};
</script>

<style scoped>
.pricing-container {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.pricing-header {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  color: white;
  text-align: center;
  box-shadow: var(--shadow-lg);
  margin-bottom: 2rem;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  margin-bottom: 0.5rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.pricing-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.pricing-header p {
  font-size: 0.95rem;
  opacity: 0.9;
  margin: 0;
}

.section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  margin-bottom: 2rem;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.section-header h2 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.tickets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  padding: 2rem;
}

.ticket-card {
  background: var(--color-background-mute);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
  overflow: hidden;
}

.ticket-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.ticket-card.inactive {
  opacity: 0.6;
}

.ticket-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem;
}

.ticket-header h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.ticket-actions {
  display: flex;
  gap: 0.5rem;
}

.ticket-content {
  padding: 0 1.5rem 1.5rem;
}

.ticket-description {
  color: var(--color-text-muted);
  font-size: 0.95rem;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.ticket-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.price {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-blue);
}

.ticket-type {
  background: var(--primary-blue);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.ticket-events {
  margin-bottom: 0.5rem;
}

.events-count {
  background: var(--success);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.ticket-status {
  text-align: right;
}

.status-active {
  color: var(--success);
  font-weight: 500;
}

.status-inactive {
  color: var(--error);
  font-weight: 500;
}

.payment-filters {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--color-border);
}

.payment-filters select {
  width: 100%;
  max-width: 300px;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
}

.payments-list {
  padding: 2rem;
}

.payment-card {
  background: var(--color-background-mute);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
  margin-bottom: 1rem;
  overflow: hidden;
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-border);
}

.payment-header h4 {
  margin: 0;
  color: var(--color-heading);
}

.status-completed {
  color: var(--success);
  font-weight: 500;
}

.status-partial {
  color: var(--warning);
  font-weight: 500;
}

.status-pending {
  color: var(--error);
  font-weight: 500;
}

.payment-details {
  padding: 1.5rem;
}

.payment-amounts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.amount-paid, .amount-due {
  text-align: center;
  padding: 1rem;
  background: var(--color-background);
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.amount-paid .amount {
  color: var(--success);
  font-size: 1.25rem;
  font-weight: 700;
}

.amount-due .amount {
  color: var(--error);
  font-size: 1.25rem;
  font-weight: 700;
}

.label {
  display: block;
  font-size: 0.9rem;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
}

.payment-info {
  margin-bottom: 1rem;
}

.payment-info p {
  margin: 0.5rem 0;
  color: var(--color-text);
}

.payment-actions {
  text-align: right;
}

.no-payments {
  text-align: center;
  padding: 3rem;
  color: var(--color-text-muted);
}

.group-price-table-wrap {
  padding: 1rem 2rem 2rem;
  overflow-x: auto;
}

.group-price-table {
  width: 100%;
  border-collapse: collapse;
}

.group-price-table th,
.group-price-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border);
  text-align: left;
}

.group-price-table th {
  color: var(--color-heading);
  font-weight: 600;
}

.group-price-table input {
  width: 160px;
  padding: 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
}

.group-name {
  font-weight: 600;
  color: var(--color-heading);
}

.btn-sm {
  padding: 0.5rem 0.9rem;
  font-size: 0.9rem;
}

.section-hint {
  padding: 0 2rem 1rem;
  color: var(--color-text-muted);
  font-size: 0.9rem;
  margin: 0;
}

/* mdl Styles */
.mdl-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mdl-content {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--color-border);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.mdl-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.mdl-header h3 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 50%;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: var(--error);
  color: white;
  border-color: var(--error);
}

.mdl-body {
  padding: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-heading);
  font-weight: 500;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
  font-size: 1rem;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.form-help {
  display: block;
  margin-top: 0.5rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  font-style: italic;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
}

/* Button styles moved to components.css */

/* Responsive Design */
@media (max-width: 768px) {
  .pricing-container {
    padding: 1rem;
  }
  
  .tickets-grid {
    grid-template-columns: 1fr;
    padding: 1rem;
  }
  
  .payment-amounts {
    grid-template-columns: 1fr;
  }
  
  .mdl-content {
    width: 95%;
    margin: 1rem;
  }
  
  .form-actions {
    flex-direction: column;
  }
}
</style>
